import React, { useState, useEffect } from 'react';
import { Button, Typography } from 'antd';
import { SettingOutlined, CloseOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useRecommendations } from '../hooks/useRecommendations';
import { ROUTES } from '../../../shared/constants/routes';

const { Text } = Typography;

const BANNER_DISMISSED_KEY = 'janus-onboarding-banner-dismissed-v1';

const OnboardingBanner: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const { data } = useRecommendations(isAuthenticated ? user?.tenantId : undefined);

  const score = data?.completudeScore ?? 0;
  const totalPending = data?.totalPending ?? 0;

  // Restore or reset dismissed state based on score
  useEffect(() => {
    if (!data) return;
    const stored = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (!stored) {
      setDismissed(false);
      return;
    }
    try {
      const { dismissedAtScore } = JSON.parse(stored) as { dismissedAtScore: number };
      // Reset if score has changed since last dismiss
      if (dismissedAtScore !== score) {
        localStorage.removeItem(BANNER_DISMISSED_KEY);
        setDismissed(false);
      } else {
        setDismissed(true);
      }
    } catch {
      setDismissed(false);
    }
  }, [data, score]);

  const handleDismiss = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, JSON.stringify({ dismissedAtScore: score }));
    setDismissed(true);
  };

  if (!isAuthenticated || !data || score >= 70 || totalPending === 0 || dismissed) {
    return null;
  }

  return (
    <div
      style={{
        background: 'linear-gradient(90deg, #eff6ff, #eef2ff)',
        borderBottom: '1px solid #c7d2fe',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        height: 48,
        gap: 12,
        flexShrink: 0,
      }}
    >
      {/* Left content */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <SettingOutlined style={{ color: '#6366f1', fontSize: 15, flexShrink: 0 }} />
        <Text
          style={{
            color: '#3730a3',
            fontSize: 13,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Sua configuração está{' '}
          <strong>{score}%</strong> completa —{' '}
          <strong>{totalPending}</strong>{' '}
          {totalPending === 1 ? 'ação' : 'ações'} para maximizar o Janusos.ai
        </Text>
      </div>

      {/* Right actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <Button
          type="link"
          size="small"
          icon={<ArrowRightOutlined />}
          iconPosition="end"
          onClick={() => navigate(ROUTES.SETTINGS_ONBOARDING)}
          style={{
            color: '#6366f1',
            fontWeight: 600,
            padding: '0 4px',
            fontSize: 13,
            height: 'auto',
          }}
        >
          Ver recomendações
        </Button>

        <Button
          type="text"
          size="small"
          icon={<CloseOutlined style={{ fontSize: 11 }} />}
          onClick={handleDismiss}
          style={{
            color: 'var(--color-text-tertiary)',
            padding: '2px 6px',
            height: 'auto',
            lineHeight: 1,
          }}
          aria-label="Fechar banner"
        />
      </div>
    </div>
  );
};

export default OnboardingBanner;
